// -----------------------------------------------------------------------------
// React Query hooks for the discovery flow (Phase 6 Wave 11.F).
// -----------------------------------------------------------------------------

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { DocumentTypeResponse } from '../../schema/api';
import { SchemaQueryKeys } from '../../schema/api';
import {
  abandonDraft,
  acceptCandidateField,
  addDiscoverySample,
  applyRecommendation,
  dismissRecommendation,
  DiscoveryQueryKeys,
  DiscoveryStatuses,
  getDiscoveryDraft,
  issueDiscoverySasForDraft,
  issuePreliminaryDiscoverySas,
  listDiscoveryDrafts,
  listImprovementSignals,
  reeducateDocumentType,
  modifyCandidateField,
  publishDraft,
  rejectCandidateField,
  startDiscovery,
  type AddSampleBody,
  type ImprovementRecommendationResponse,
  type IntelligentDraftResponse,
  type IntelligentDraftSummaryResponse,
  type IssueDiscoverySasResponse,
  type ModifyCandidateFieldRequest,
  type PublishFromDraftRequest,
  type StartDiscoveryRequest,
} from './api';

/** Lists every active draft for the tenant. */
export function useDraftList(): UseQueryResult<IntelligentDraftSummaryResponse[]> {
  return useQuery({
    queryKey: DiscoveryQueryKeys.drafts(),
    queryFn: ({ signal }) => listDiscoveryDrafts(signal),
  });
}

/** Single-draft fetcher. Pass `undefined` to skip the query. */
export function useDraft(
  draftId: string | undefined,
): UseQueryResult<IntelligentDraftResponse> {
  return useQuery({
    queryKey: draftId !== undefined ? DiscoveryQueryKeys.draft(draftId) : ['discovery', 'draft', '__skip__'],
    queryFn: ({ signal }) => getDiscoveryDraft(draftId as string, signal),
    enabled: draftId !== undefined,
    // Force a fresh fetch on every mount. The Analyzing step shares this key
    // and stops polling the moment the status flips off `Analyzing`; without
    // staleTime: 0 the Review step would mount reading the last cached
    // snapshot — which may still carry candidateFields = [].
    staleTime: 0,
  });
}

/**
 * Polls a draft every 3s while it is in the `Analyzing` state — when it
 * transitions out, React Query stops refetching automatically (the
 * `refetchInterval` callback returns `false`).
 */
export function useDraftPolling(
  draftId: string | undefined,
): UseQueryResult<IntelligentDraftResponse> {
  return useQuery({
    queryKey: draftId !== undefined ? DiscoveryQueryKeys.draft(draftId) : ['discovery', 'draft', '__skip__'],
    queryFn: ({ signal }) => getDiscoveryDraft(draftId as string, signal),
    enabled: draftId !== undefined,
    refetchInterval: (query) => {
      const data = query.state.data as IntelligentDraftResponse | undefined;
      if (data === undefined) {
        return 2000;
      }
      return data.status === DiscoveryStatuses.Analyzing ? 2000 : false;
    },
  });
}

function invalidateDraft(
  queryClient: ReturnType<typeof useQueryClient>,
  draftId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.draft(draftId) });
  void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.maturity(draftId) });
  void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.drafts() });
}

export function useStartDiscovery(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  StartDiscoveryRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: StartDiscoveryRequest) => startDiscovery(request),
    onSuccess: (draft) => {
      queryClient.setQueryData(DiscoveryQueryKeys.draft(draft.id), draft);
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.drafts() });
    },
  });
}

export function useAddSample(
  draftId: string,
): UseMutationResult<IntelligentDraftResponse, Error, AddSampleBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddSampleBody) => addDiscoverySample(draftId, body),
    // Seed the cache with the mutation's response (the draft is already Analyzing server-side)
    // instead of invalidate-only: the upload page navigates to the analyzing step in the same
    // tick, and its redirect effect would read the STALE cached draft (still Created) and bounce
    // straight to review before the refetch lands.
    onSuccess: (draft) => {
      queryClient.setQueryData(DiscoveryQueryKeys.draft(draft.id), draft);
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.drafts() });
    },
  });
}

export interface FieldDecisionVariables {
  draftId: string;
  fieldId: string;
}

export function useAcceptField(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  FieldDecisionVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, fieldId }) => acceptCandidateField(draftId, fieldId),
    onSuccess: (_, { draftId }) => invalidateDraft(queryClient, draftId),
  });
}

export function useRejectField(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  FieldDecisionVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, fieldId }) => rejectCandidateField(draftId, fieldId),
    onSuccess: (_, { draftId }) => invalidateDraft(queryClient, draftId),
  });
}

export interface ModifyFieldVariables extends FieldDecisionVariables {
  request: ModifyCandidateFieldRequest;
}

export function useModifyField(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  ModifyFieldVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, fieldId, request }) =>
      modifyCandidateField(draftId, fieldId, request),
    onSuccess: (_, { draftId }) => invalidateDraft(queryClient, draftId),
  });
}

export interface PublishDraftVariables {
  draftId: string;
  request: PublishFromDraftRequest;
}

export function usePublishDraft(): UseMutationResult<
  DocumentTypeResponse,
  Error,
  PublishDraftVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, request }) => publishDraft(draftId, request),
    onSuccess: (created, { draftId }) => {
      invalidateDraft(queryClient, draftId);
      // The new published type joins the schema list — refresh it so the
      // operator lands on a list that already contains the new entry.
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.list() });
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.detail(created.slug) });
    },
  });
}

/**
 * Lazy-loads improvement signals per slug. Returns an empty list (rather than
 * undefined) once the request completes — callers use `.length === 0` to hide
 * surfaces that should not render without signals.
 */
export function useImprovementSignals(
  slug: string | undefined,
): UseQueryResult<ImprovementRecommendationResponse[]> {
  return useQuery({
    queryKey: slug !== undefined ? DiscoveryQueryKeys.signals(slug) : ['discovery', 'signals', '__skip__'],
    queryFn: ({ signal }) => listImprovementSignals(slug as string, signal),
    enabled: slug !== undefined,
    // Signals are derived server-side from learning events; refresh on a
    // generous cadence and don't refetch on every focus to keep list views light.
    staleTime: 60_000,
  });
}

export interface ApplyRecommendationVariables {
  slug: string;
  signalId: string;
  hint: string;
}

/** Applies a surfaced suggestion's hint to the field and refreshes the signal + schema caches. */
export function useApplyRecommendation(): UseMutationResult<
  DocumentTypeResponse,
  Error,
  ApplyRecommendationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, signalId, hint }: ApplyRecommendationVariables) =>
      applyRecommendation(slug, signalId, hint),
    onSuccess: (created, { slug }) => {
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.signals(slug) });
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.detail(created.slug) });
    },
  });
}

export interface DismissRecommendationVariables {
  slug: string;
  signalId: string;
}

/** Dismisses a surfaced signal and refreshes the signal list. */
export function useDismissRecommendation(): UseMutationResult<
  void,
  Error,
  DismissRecommendationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, signalId }: DismissRecommendationVariables) =>
      dismissRecommendation(slug, signalId),
    onSuccess: (_, { slug }) => {
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.signals(slug) });
    },
  });
}

export interface ReeducateVariables {
  slug: string;
  /** Optional LLM override; null/undefined = the type's own configured model. */
  modelProviderId?: string | null;
}

export function useReeducateDocumentType(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  ReeducateVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, modelProviderId }: ReeducateVariables) =>
      reeducateDocumentType(slug, modelProviderId),
    onSuccess: (draft) => {
      queryClient.setQueryData(DiscoveryQueryKeys.draft(draft.id), draft);
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.drafts() });
    },
  });
}

/**
 * Deferral D4 — mints write SAS URIs the browser uses to upload discovery
 * samples directly to Blob Storage. Pass `draftId=undefined` to mint a batch
 * against a preliminary draft id (file goes up before the draft is created).
 */
export interface IssueDiscoverySasVariables {
  draftId?: string;
  count: number;
}

export function useIssueDiscoverySas(): UseMutationResult<
  IssueDiscoverySasResponse,
  Error,
  IssueDiscoverySasVariables
> {
  return useMutation({
    mutationFn: ({ draftId, count }) =>
      draftId !== undefined
        ? issueDiscoverySasForDraft(draftId, count)
        : issuePreliminaryDiscoverySas(count),
  });
}

/** Deferral D6 — abandons a draft and triggers tenant-scoped blob cleanup. */
export function useAbandonDraft(): UseMutationResult<
  IntelligentDraftResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) => abandonDraft(draftId),
    onSuccess: (draft) => {
      queryClient.setQueryData(DiscoveryQueryKeys.draft(draft.id), draft);
      void queryClient.invalidateQueries({ queryKey: DiscoveryQueryKeys.drafts() });
    },
  });
}
